import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../hooks/useAppDispatch';
import AuthorizedSignCheckbox from '../authorized-sign-checkbox/AuthorizedSignCheckbox';
import BadgeIcon from '../../../assets/svg/badge.svg?react';
import styles from './CustomerContactCard.module.scss';

import { selectBusinessProfileStatus } from '../../slices/business-profile/businessProfileSlice';
import { useAppSelector } from '../hooks/useAppSelector';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import { CustomerContactItem } from '../../models/customer.contact.model';
import {
  deleteCustomerContacts,
  updateCustomerContacts,
} from '../../slices/customers/customersSlice';
import { EditButtonsRow } from './EditButtonsRow';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface SupplierContactCardProps {
  contact: CustomerContactItem;
  companyId: number;
}

export default function CustomerContactCard({ contact, companyId }: SupplierContactCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const isLoading = businessProfileStatus === 'loading';

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const saveChanges = () => {
    if (!contact?.id) return;
    dispatch(
      updateCustomerContacts({
        companyId: companyId,
        contact: formData,
        customerId: formData.customer_id,
      })
    );

    setIsEdited(false);
    setShowModal(false);
  };

  const handleDelete = () => {
    if (!contact?.id) return;
    dispatch(showOverlay());
    dispatch(
      deleteCustomerContacts({
        companyId: companyId,
        contactId: contact.id,
        customerId: formData.customer_id,
      })
    )
      .unwrap()
      //.then(() => dispatch(fetchSupplierById(formData.supplier_id)))
      .finally(() => dispatch(hideOverlay()));
    setIsEdited(false);
  };

  const confirmExit = () => {
    setIsEdited(false);
    setShowModal(false);
  };

  const [formData, setFormData] = useState<CustomerContactItem>({
    id: contact.id,
    full_name: contact.full_name,
    email_address: contact.email_address,
    postal_address: contact.postal_address,
    phone_number: contact.phone_number,
    job_title: contact.job_title,
    authorized_rep: contact.authorized_rep,
    customer_id: contact.customer_id,
  });

  const handleChange =
    <K extends keyof CustomerContactItem>(field: K) =>
    (value: CustomerContactItem[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const cancelChanges = () => {
    setIsEdited(false);
    if (contact && contact.id) {
      setFormData({
        id: contact.id,
        full_name: contact.full_name,
        email_address: contact.email_address,
        postal_address: contact.postal_address,
        phone_number: contact.phone_number,
        job_title: contact.job_title,
        authorized_rep: contact.authorized_rep,
        customer_id: contact.customer_id,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!autocompleteRef.current || !autocompleteRef.current.contains(event.target as Node)) &&
        (!popperRef.current || !popperRef.current.contains(event.target as Node))
      ) {
        if (event.target instanceof HTMLElement) {
          const ignoredElements = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL'];
          if (ignoredElements.includes(event.target.tagName)) return;
        }

        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited]);

  return (
    <Card
      className={`inputs-editable ${styles.card} ${isEdited ? styles.card_border : ''}`}
      ref={formRef}
    >
      <CardContent
        className={isLoading ? styles.card_content_min_height : ''}
        sx={{ paddingTop: 0, paddingBottom: '16px !important' }}
      >
        <Grid container>
          <Grid container width='100%' alignItems='center' p={2}>
            <Grid size={{ xs: 10 }} alignItems='center' gap={1}>
              <Grid container alignItems='center' gap={1}>
                <Grid>
                  <BadgeIcon />
                </Grid>
                <Grid>
                  <Typography variant='subtitle1'>{contact.full_name}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 2 }} container justifyContent='flex-end'>
              {!isEdited ? (
                <EditButtonsRow onEdit={toggleEditing} onDelete={handleDelete} />
              ) : (
                <Box display='flex' gap='8px' alignItems='center'>
                  <Button
                    type='button'
                    className='btn-default btn-action-basic btn-cancel'
                    role='button'
                    variant='contained'
                    size='large'
                    onClick={cancelChanges}
                  >
                    {t('buttons.cancel')}
                  </Button>
                  <Button
                    type='submit'
                    className='btn-default btn-action-basic'
                    role='button'
                    disabled={!isEdited}
                    variant='contained'
                    size='large'
                    onClick={saveChanges}
                  >
                    {t('buttons.save')}
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>

          <Grid container width='100%' spacing={2}>
            <Grid size={{ xs: 12, md: 10 }} container spacing={2}>
              {isEdited && (
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label={`${t('labels.fullName')} *`}
                    variant='outlined'
                    fullWidth
                    value={formData.full_name ?? ''}
                    onChange={(e) => handleChange('full_name')(e.target.value)}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  title={formData.email_address}
                  label={t('formInputs.emailAddressInputLabel')}
                  variant='outlined'
                  type='email'
                  fullWidth
                  value={formData.email_address ?? ''}
                  onChange={(e) => handleChange('email_address')(e.target.value)}
                  disabled={!isEdited}
                />
                {formData.email_address && (
                  <Box>
                    <Button
                      variant='text'
                      style={{ textAlign: 'left' }}
                      onClick={() => navigator.clipboard.writeText(formData.email_address)}
                    >
                      {t('buttons.copyEmailToClickBoard')}
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label={t('labels.phoneNumber')}
                  variant='outlined'
                  type='tel'
                  fullWidth
                  value={formData.phone_number ?? ''}
                  onChange={(e) => handleChange('phone_number')(e.target.value)}
                  disabled={!isEdited}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label={t('labels.jobTitle')}
                  variant='outlined'
                  fullWidth
                  value={formData.job_title ?? ''}
                  disabled={!isEdited}
                  onChange={(e) => handleChange('job_title')(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label={t('labels.postalAddress')}
                  variant='outlined'
                  fullWidth
                  value={formData.postal_address ?? ''}
                  disabled={!isEdited}
                  onChange={(e) => handleChange('postal_address')(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container width='100%' spacing={2} className={styles.checkbox_font}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <AuthorizedSignCheckbox
                authorizedRep
                value={formData.authorized_rep}
                onChange={handleChange('authorized_rep')}
                disabled={!isEdited}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardContent>

      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Card>
  );
}
